import { ArrowRight, Send, X } from 'lucide-react';
import { FaXTwitter, FaInstagram, FaLinkedin, FaTelegram } from 'react-icons/fa6';
import { useState } from 'react';

// Components
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ReferralSection from '../components/ReferralSection';

// Context
import { useLanguage } from '../context/LanguageContext';

// Utils
import api from '../utils/api';

// Data - Same data source as AllServicesPage
import { servicesLeft, servicesRightTop, servicesRightBottom } from '../data/services';
import { serviceBlocksData } from '../data/serviceBlocks';

// Service Popup Modal Component
const ServicePopup = ({ service, onClose, t }) => {
  if (!service) return null;

  const accentColors = {
    purple: {
      text: 'text-purple-400',
      border: 'border-purple-500/30',
      bg: 'bg-purple-500/10',
      button: 'bg-purple-600 hover:bg-purple-500',
      bullet: 'bg-purple-500'
    },
    yellow: {
      text: 'text-yellow-400',
      border: 'border-yellow-500/30',
      bg: 'bg-yellow-500/10',
      button: 'bg-yellow-600 hover:bg-yellow-500',
      bullet: 'bg-yellow-500'
    },
    cyan: {
      text: 'text-cyan-400',
      border: 'border-cyan-500/30',
      bg: 'bg-cyan-500/10',
      button: 'bg-cyan-600 hover:bg-cyan-500',
      bullet: 'bg-cyan-500'
    }
  };

  const colors = accentColors[service.accentColor] || accentColors.purple;

  // Get the image source - handle both formats
  const imageSrc = service.image?.src || service.image;
  const hasImage = imageSrc && typeof imageSrc === 'string';

  // Get translated content based on service id
  const getTranslatedContent = () => {
    const blockMap = {
      'realestate-fund': 'realEstate',
      'quant-fund': 'quant',
      'crypto': 'crypto',
      'tax': 'tax',
      'offmarket': 'offmarket',
      'gold': 'gold',
      'supercars': 'supercars',
      'yachts': 'yachts',
      'watches': 'watches',
      'art': 'art'
    };
    const key = blockMap[service.id];
    if (key) {
      return {
        title: t(`serviceBlocks.${key}.title`),
        subtitle: t(`serviceBlocks.${key}.subtitle`),
        description: t(`serviceBlocks.${key}.description`),
        bullets: [
          t(`serviceBlocks.${key}.bullet1`),
          t(`serviceBlocks.${key}.bullet2`),
          t(`serviceBlocks.${key}.bullet3`),
          t(`serviceBlocks.${key}.bullet4`)
        ].filter(b => b && !b.includes('serviceBlocks')),
        cta: t(`serviceBlocks.${key}.cta`)
      };
    }
    return null;
  };

  const translated = getTranslatedContent();
  const bullets = translated?.bullets?.length > 0 ? translated.bullets : service.bulletPoints;

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-gray-900 border border-gray-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-gray-800 hover:bg-gray-700 text-white p-2 rounded-full transition-colors"
          data-testid="popup-close"
        >
          <X size={24} />
        </button>

        {/* Image - Taller aspect ratio with lazy loading */}
        {hasImage && (
          <div className="w-full h-52 md:h-64 overflow-hidden rounded-t-2xl bg-gray-800">
            <img 
              src={imageSrc} 
              alt={service.image?.alt || service.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        )}

        {/* Content - More spacing */}
        <div className="p-8 md:p-10">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <span className={`${colors.text} text-sm font-mono font-bold`}>{service.number}</span>
              <span className={`${colors.text} text-sm`}>•</span>
              <span className={`${colors.text} text-sm`}>{translated?.subtitle || service.subtitle}</span>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-white">{translated?.title || service.title}</h2>
          </div>

          {/* Description */}
          <p className="text-gray-300 text-sm md:text-base leading-relaxed mb-8">
            {translated?.description || service.description}
          </p>

          {/* Special highlight for Tax - OnlyFans elegant note */}
          {service.id === 'tax' && (
            <div className="border-l-2 border-yellow-500 pl-4 mb-8 bg-yellow-500/5 py-2">
              <p className="text-yellow-400/90 text-sm italic">
                OnlyFans & Content Creators → Legal 0% Tax Structures
              </p>
            </div>
          )}

          {/* Bullet Points - Professional format without arrows */}
          {bullets && bullets.length > 0 && (
            <ul className="space-y-3 mb-10">
              {bullets.map((point, index) => (
                <li key={index} className="flex items-center gap-3 text-sm md:text-base">
                  <span className={`w-1.5 h-1.5 rounded-full ${colors.bullet} flex-shrink-0`}></span>
                  <span className="text-gray-300">{point}</span>
                </li>
              ))}
            </ul>
          )}

          {/* CTA Buttons - Centered */}
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            {service.cta && (
              <a
                href={service.cta.href}
                target={service.cta.external ? '_blank' : undefined}
                rel={service.cta.external ? 'noopener noreferrer' : undefined}
                className={`${colors.button} text-white px-8 py-3 rounded-lg inline-flex items-center justify-center font-semibold transition-all text-sm`}
              >
                {translated?.cta || service.cta.text}
                <ArrowRight className="ml-2" size={16} />
              </a>
            )}
            {service.ctaInvest && (
              <a
                href={service.ctaInvest.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  api.trackClick({
                    event_type: 'invest_now_whatsapp',
                    source: 'services2_popup',
                    button_id: `invest-popup-${service.id}`
                  });
                }}
                className="bg-green-600 hover:bg-green-500 text-white px-8 py-3 rounded-lg inline-flex items-center justify-center font-semibold transition-all text-sm"
                data-testid={`popup-invest-btn-${service.id}`}
              >
                {service.ctaInvest.text}
                <svg className="ml-2 w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ServicesPage2 = () => {
  const [selectedService, setSelectedService] = useState(null);
  const { t } = useLanguage();

  const openPopup = (serviceId) => {
    const service = serviceBlocksData.find(s => s.id === serviceId);
    if (service) {
      setSelectedService(service);
    }
  };

  const closePopup = () => {
    setSelectedService(null);
  };

  // Render service card
  const ServiceCard = ({ service, index, accentColor = 'purple', featured = false }) => {
    const baseClasses = "hover:bg-gray-700 border px-3 md:px-4 py-3 md:py-4 rounded-lg md:rounded-xl text-left transition-all group h-auto md:h-[85px] w-full";
    const featuredClasses = "bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border-yellow-500/50 hover:border-yellow-400";
    const normalPurple = "bg-gray-800 border-gray-700 hover:border-purple-500/50";
    const normalYellow = "bg-gray-800 border-gray-700 hover:border-yellow-500/50";
    
    const cardClasses = featured 
      ? `${baseClasses} ${featuredClasses}`
      : `${baseClasses} ${accentColor === 'yellow' ? normalYellow : normalPurple}`;

    // Featured card has purple number, others follow accentColor
    const numberColor = featured ? 'text-purple-400' : (accentColor === 'yellow' ? 'text-yellow-500' : 'text-purple-400');
    const hoverColor = featured || accentColor === 'yellow' ? 'group-hover:text-yellow-400' : 'group-hover:text-purple-400';
    const displayNumber = service.num || String(index + 1).padStart(2, '0');

    return (
      <button
        onClick={() => openPopup(service.id)}
        className={cardClasses}
        data-testid={`service-card-${service.id}`}
      >
        <div className="flex items-center gap-1 md:gap-2">
          <span className={`${numberColor} text-xs md:text-sm font-mono font-bold`}>{displayNumber}</span>
          <h3 className={`text-white font-bold text-[10px] md:text-sm ${hoverColor} transition-colors leading-tight`}>{service.name}</h3>
        </div>
        <p className="text-gray-400 text-[9px] md:text-xs mt-1 md:mt-2 ml-4 md:ml-7 leading-tight hidden md:block">
          {service.badge && <span className="text-yellow-400 font-semibold">{service.badge} → </span>}
          {service.subtitle}
        </p>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gray-950" data-testid="services-page-2">
      <Navbar />

      {/* Service Popup */}
      {selectedService && (
        <ServicePopup service={selectedService} onClose={closePopup} t={t} />
      )}
      
      {/* Hero Section - Full Height */}
      <section className="relative min-h-screen flex items-center justify-center pt-28 pb-8 bg-gray-950">
        <img 
          src="https://customer-assets.emergentagent.com/job_realestate-fund-6/artifacts/qsxu12yq_1_LE_upscale_balanced_x4.webp"
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950/80 via-gray-950/70 to-gray-950"></div>
        
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 text-center">
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
            {t('services2.title')}
          </h1>
          
          <p className="text-sm md:text-lg text-gray-300 mb-2 md:mb-3 max-w-3xl mx-auto leading-relaxed italic">
            {t('services2.founderDesc')}
          </p>
          <p className="text-sm md:text-lg text-gray-300 mb-4 md:mb-6 max-w-3xl mx-auto leading-relaxed italic">
            {t('services2.networkDesc')}
          </p>
          
          {/* Current Focus */}
          <div className="bg-gradient-to-r from-yellow-500/20 via-yellow-400/10 to-yellow-500/20 border border-yellow-500/30 rounded-xl px-4 md:px-6 py-3 md:py-4 mb-4 md:mb-8 max-w-2xl mx-auto">
            <p className="text-yellow-400 font-bold text-xs md:text-sm tracking-wide mb-1">{t('services2.currentFocus')}: "AAC REAL ESTATE I"</p>
            <p className="text-white text-base md:text-lg font-semibold">15-40% annualized returns</p>
            <p className="text-gray-300 text-xs md:text-sm">{t('services2.lookingInvestors')} · <span className="text-yellow-400">{t('services2.referralAvailable')}</span></p>
          </div>
          
          {/* Social Links */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8 md:mb-12">
            <a href="https://x.com/lil_jimmo" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 md:gap-2 bg-gray-800/80 hover:bg-gray-700 text-white px-3 md:px-4 py-2 md:py-3 rounded-lg transition-colors text-sm">
              <FaXTwitter size={16} /> <span className="hidden md:inline">@lil_jimmo</span>
            </a>
            <a href="https://www.instagram.com/accretion.capital/" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 bg-gray-800/80 hover:bg-gray-700 text-white px-3 md:px-4 py-2 md:py-3 rounded-lg transition-colors">
              <FaInstagram size={16} />
            </a>
            <a href="https://www.linkedin.com/company/accretion-axiom-capital/" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 bg-gray-800/80 hover:bg-gray-700 text-white px-3 md:px-4 py-2 md:py-3 rounded-lg transition-colors">
              <FaLinkedin size={16} />
            </a>
            <a href="https://t.me/+gbLD_8i3AbIzMTY0" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 md:gap-2 bg-purple-600/80 hover:bg-purple-500 text-white px-3 md:px-4 py-2 md:py-3 rounded-lg transition-colors text-sm">
              <FaTelegram size={16} /> <span className="hidden md:inline">Deals Group</span>
            </a>
          </div>
          
          {/* Service Cards Grid */}
          <div className="bg-gray-900/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 w-full">
            <p className="text-center text-[10px] md:text-xs tracking-[0.15em] md:tracking-[0.2em] text-gray-500 mb-4 md:mb-6 mt-1 md:mt-2">
              {t('services2.clickToView')}
            </p>
            
            <div className="flex flex-col lg:flex-row gap-6 justify-center items-stretch">
              {/* Left Column - Investment & Financial */}
              <div className="lg:w-[48%]">
                <p className="text-xs text-purple-400 font-medium mb-4 tracking-wider text-center lg:text-right">
                  {t('services2.investmentFinancial')}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {servicesLeft.map((service, index) => (
                    <ServiceCard 
                      key={service.id} 
                      service={service} 
                      index={index}
                      accentColor="purple"
                      featured={index === 0}
                    />
                  ))}
                </div>
              </div>
              
              {/* Vertical Line - Centered */}
              <div className="hidden lg:flex items-center">
                <div className="w-px h-full bg-gray-700"></div>
              </div>
              
              {/* Right Column - Luxury & Assets */}
              <div className="lg:w-[48%]">
                <p className="text-xs text-yellow-500 font-medium mb-4 tracking-wider text-center lg:text-left">
                  {t('services2.luxuryAssets')}
                </p>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  {[...servicesRightTop, ...servicesRightBottom].map((service) => (
                    <ServiceCard key={service.id} service={service} accentColor="yellow" />
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Subtle scroll hint - outside the box */}
          <div className="flex justify-center mt-8 opacity-40 hover:opacity-70 transition-opacity">
            <svg className="w-5 h-5 text-gray-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </section>

      {/* Referral Section */}
      <ReferralSection />

      {/* Final CTA */}
      <section className="py-20 bg-gray-950 border-t border-gray-800">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">{t('services2.readyConnect')}</h2>
          <p className="text-gray-400 mb-8">
            {t('services2.contactDesc')}
          </p>
          <a 
            href="https://t.me/+gbLD_8i3AbIzMTY0" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-purple-600 hover:bg-purple-500 text-white px-10 py-4 rounded-lg inline-flex items-center font-bold text-lg transition-all"
          >
            <Send className="mr-3" size={20} />
            {t('services2.contactTelegram')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage2;
